import React from 'react';
import { SeoHero } from '@/components/ui/seo-hero';
import { Badge } from '@/components/ui/badge';
import { ClientLogosSlider } from '@/components/ui/ClientLogosSlider';
import SeoProcessSection from '@/components/SeoProcessSection';
import SeoTestimonialsSection from '@/components/SeoTestimonialsSection';
import SeoGettingStartedSection from '@/components/SeoGettingStartedSection';
import SeoFaqSection from '@/components/SeoFaqSection';
import FinalCTA from '@/components/FinalCTA';
import Navigation from '@/components/Navigation';
import FooterWithNewsletter from '@/components/ui/footer-column';
import SEO from '@/components/SEO';
import { useSEO } from '@/hooks/useSEO';
import { getSEOConfigForPage } from '@/utils/seoUtils';
import { Lightbulb, Users, Calendar, Edit, Shield, Target, Sparkles } from 'lucide-react';

const SeoLandingPage = () => {
  const { fallbackMetadata } = getSEOConfigForPage('/create-social-content-calendars-with-ai');
  const { seoData, canonicalUrl, noindex, nofollow } = useSEO({ fallbackMetadata });

  const socialCalendarSteps = [
    {
      icon: Lightbulb,
      title: "Brainstorm content ideas with Studio",
      description: "Kick off ideation in gimmefy's Studio—just drop a few prompts and instantly generate a tailored list of relevant, on-brand content angles, trending topics, and seasonal hooks for your audiences."
    },
    {
      icon: Users,
      title: "Create content pillars with Social Media Agent",
      description: "Use the specialist Social Media Agent to shape your brainstormed ideas into clear content pillars—think brand stories, product features, tips, testimonials, and campaigns, mapped to each social channel."
    },
    {
      icon: Calendar,
      title: "Refine pillars into a full calendar",
      description: "Let AI transform your pillars into a daily, weekly, or campaign-based calendar—auto-scheduling posts, mixing formats (reels, stories, carousels, text), and filling in gaps for consistent output across every platform."
    },
    {
      icon: Edit,
      title: "Edit on the fly, polish, ready to go live",
      description: "Tweak any copy, or visuals using gimmefy's Visual Editor. Keep repeating, and your entire social calendar for a month is ready—in about an hour."
    }
  ];

  const socialCalendarGettingStartedSteps = [
    {
      icon: Shield,
      title: "Create your Brand Vault",
      description: "Lock in your voice, visual style, and messaging rules.",
      time: "2 mins",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
      border: "border-blue-200/50 dark:border-blue-800/50",
      numberBg: "bg-blue-100 dark:bg-blue-900/50",
      numberBorder: "border-blue-300/50 dark:border-blue-700/50",
      numberText: "text-blue-700 dark:text-blue-300",
      timeBg: "bg-blue-600 dark:bg-blue-500",
      timeText: "text-white"
    },
    {
      icon: Target,
      title: "Generate your pillars",
      description: "Generate ideas and pillars, as per your social goals, and plan calendars in minutes.",
      time: "5 minutes",
      gradient: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
      border: "border-emerald-200/50 dark:border-emerald-800/50",
      numberBg: "bg-emerald-100 dark:bg-emerald-900/50",
      numberBorder: "border-emerald-300/50 dark:border-emerald-700/50",
      numberText: "text-emerald-700 dark:text-emerald-300",
      timeBg: "bg-emerald-600 dark:bg-emerald-500",
      timeText: "text-white"
    },
    {
      icon: Sparkles,
      title: "Create your content",
      description: "Go from ideas to execution with Studio to come up with copy and visuals, ready to roll.",
      time: "varies",
      gradient: "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30",
      border: "border-orange-200/50 dark:border-orange-800/50",
      numberBg: "bg-orange-100 dark:bg-orange-900/50",
      numberBorder: "border-orange-300/50 dark:border-orange-700/50",
      numberText: "text-orange-700 dark:text-orange-300",
      timeBg: "bg-orange-600 dark:bg-orange-500",
      timeText: "text-white"
    }
  ];

  const socialCalendarFaqs = [
    {
      question: "Can I batch create a whole month's social posts?",
      answer: "Yes. With Magic Prompt and Playbooks, you can generate weeks of platform-ready posts in minutes."
    },
    {
      question: "Can my whole team collaborate without losing track?",
      answer: "Yes. Teams can work together inside shared workspaces, with Brand Vaults keeping voice and visuals consistent."
    },
    {
      question: "Can I post multiple platforms in one place?",
      answer: "Yes. gimmefy has built-in publishing that's available as a paid feature for any paid subscription. You can create, plan, and post content for multiple platforms—LinkedIn, Instagram, TikTok, Facebook, and more—within one workspace, ensuring consistency across channels."
    },
    {
      question: "What if I get stuck for creative ideas?",
      answer: "Magic Prompt and Playbooks suggest seasonal hooks, trending themes, and fresh campaign angles—so you never start from a blank page."
    },
    {
      question: "Can gimmefy help with holidays and special campaigns?",
      answer: "Yes. You can add campaign dates, events, or seasonal hooks, and gimmefy will generate customised post ideas aligned to those occasions."
    },
    {
      question: "Does it automate repetitive or recurring content?",
      answer: "Yes. Playbooks allow you to run recurring workflows—like \"weekly posts\" or \"monthly recaps\"—so you can refresh and re-use formats without starting from scratch."
    },
    {
      question: "How does post frequency get managed?",
      answer: "gimmefy won't auto-pace publishing, but you can set volume goals and generate posts in batches. From there, you manage posting cadence inside your scheduling tool of choice."
    },
    {
      question: "Can my team review posts in-platform?",
      answer: "Yes. All results can be viewed by team members using the same workspace. Team members can also refine content together, ensuring brand consistency before export."
    },
    {
      question: "Is my data secure and private?",
      answer: "Yes. Brand Vaults and workspaces are private to your team, and data is protected with enterprise-grade security and compliance standards."
    }
  ];

  return (
    <>
      {seoData && (
        <SEO 
          data={seoData} 
          canonicalUrl={canonicalUrl}
          noindex={noindex}
          nofollow={nofollow}
        />
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <SeoHero
          badge={
            <Badge className="bg-primary/10 text-primary border-primary/20 text-lg px-6 py-3 rounded-full font-medium shadow-lg backdrop-blur-sm">
              🚀 AI-Powered Content Creation That Saves Time
            </Badge>
          }
          mainTitle="Build Content Calendars with AI"
          title="Create content for one month in one hour"
          subtitle="Never run out of content ideas or face empty weeks again; let AI handle the heavy lifting. Plan, create, and schedule every post, campaign, and story from one powerful platform."
          primaryCta={{
            label: "Start for FREE",
            href: "/signup/contentcal"
          }}
          image={{
            src: "/lovable-uploads/social-growth-hero.png",
            alt: "Content calendar planning interface with AI-generated posts, scheduling tools, and organized monthly content layout"
          }}
        />

        <ClientLogosSlider />
        
        <SeoProcessSection 
          title="How it works: social content calendar creation in four steps"
          subtitle="Follow these simple steps to create professional social content calendars with AI"
          steps={socialCalendarSteps}
        />
        
        <SeoTestimonialsSection />
        
        <SeoGettingStartedSection 
          title="Getting started: set up your AI calendar in minutes"
          subtitle="Follow these simple steps to transform your social media strategy with AI-powered content creation"
          steps={socialCalendarGettingStartedSteps}
        />
        
        <FinalCTA 
          title="Experience the platform built for <br/> speed, strategy, and seamless planning." 
          subtitle="Your calendar, always ready. Your content, always on point." 
          signupUrl="/signup/contentcal"
        />
        
        <SeoFaqSection faqs={socialCalendarFaqs} />
        
        <FooterWithNewsletter />
      </div>
    </>
  );
};

export default SeoLandingPage;